// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FC, useEffect, useMemo } from 'react';
import type { AuthUser } from '@shared/data-access/interfaces/redux';
import { ReactComponent as LogoCitec } from '@advisor-app/assets/img/logo-citec.svg';
import { ReactComponent as LogoNuvara } from '@shared/ui-kit/assets/img/logo-nuvara360-light.svg';
import { EditableText } from '@advisor-app/components/editable-text';
import { useAccount } from '../hooks/use-account';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { getBrandDomainByHostname } from '@shared/environment';
import { formatDate } from '@shared/ui-kit/utils/format-date';
import { useTranslation } from 'react-i18next';

export interface UserInformationProps {
  user: AuthUser;
  expirationTimestamp: number;
}

export const UserInformation: FC<UserInformationProps> = ({
  user,
  expirationTimestamp,
}) => {
  const { accountData, handleEditAccountField, handleUpdateUserInfo } =
    useAccount();

  const domain = useMemo(
    () =>
      typeof window === 'undefined'
        ? 'citec'
        : getBrandDomainByHostname(window.location.hostname),
    [],
  );

  const { i18n } = useTranslation();

  const expirationDateFormatted = useMemo(
    () => formatDate(expirationTimestamp, 'standard', i18n.language) ?? '',
    [expirationTimestamp, i18n.language],
  );

  useEffect(() => {
    handleUpdateUserInfo();
  }, [accountData, handleUpdateUserInfo]);

  return (
    <div className="self-center bg-page text-secondary-dark">
      <div className="flex select-none flex-col items-center justify-center text-title-alt">
        <header className="flex flex-col items-center justify-center space-y-7">
          {domain === 'nuvara' ? (
            <LogoNuvara className="h-12 w-[182px] text-black" />
          ) : (
            <LogoCitec className="h-12 w-[182px] text-black" />
          )}
          <h3 className="text-center text-2xl font-semibold text-title-alt">
            <Translation i18nKey="common.account-details" ns="common" />
          </h3>
        </header>

        <dl className="mt-8 flex flex-col justify-center text-center">
          <dt className="text-border-dark">
            <Translation i18nKey="common.first-name" ns="common" />
          </dt>
          <dd className="mb-4 rounded-[20px] bg-section px-7 py-1.5 text-xl font-medium capitalize">
            <EditableText
              text={accountData.first_name || (user?.nickname as string)}
              onEdit={text => handleEditAccountField('first_name', text)}
              headingVariant="h5"
            />
          </dd>

          <dt className="text-border-dark">
            <Translation i18nKey="common.second-name" ns="common" />
          </dt>
          <dd className="mb-4 rounded-[20px] bg-section px-7 py-1.5 text-xl font-medium capitalize">
            <EditableText
              text={accountData.second_name}
              onEdit={text => handleEditAccountField('second_name', text)}
              headingVariant="h5"
            />
          </dd>

          <dt className="text-border-dark">
            <Translation i18nKey="common.first-surname" ns="common" />
          </dt>
          <dd className="mb-4 rounded-[20px] bg-section px-7 py-1.5 text-xl font-medium capitalize">
            <EditableText
              text={accountData.first_surname}
              onEdit={text => handleEditAccountField('first_surname', text)}
              headingVariant="h5"
            />
          </dd>

          <dt className="text-border-dark">
            <Translation i18nKey="common.second-surname" ns="common" />
          </dt>
          <dd className="mb-4 rounded-[20px] bg-section px-7 py-1.5 text-xl font-medium capitalize">
            <EditableText
              text={accountData.second_surname}
              onEdit={text => handleEditAccountField('second_surname', text)}
              headingVariant="h5"
            />
          </dd>

          <dt className="text-border-dark">
            <Translation i18nKey="common.phone-number" ns="common" />
          </dt>
          <dd className="mb-4 rounded-[20px] bg-section px-7 py-1.5 text-xl font-medium capitalize">
            <EditableText
              text={accountData.phone_number}
              onEdit={text => handleEditAccountField('phone_number', text)}
              headingVariant="h5"
            />
          </dd>

          <dt className="mt-6 text-border-dark">
            <Translation i18nKey="common.email-address" ns="common" />
          </dt>
          <dd className="mt-3 text-xl font-medium">{user?.email}</dd>

          <dt className="mt-6 text-border-dark">
            <Translation i18nKey="common.expiration-date" ns="common" />
          </dt>
          <dd className="mt-3 text-xl font-medium"> {expirationDateFormatted}</dd>
        </dl>
      </div>
    </div>
  );
};
